import jsPDF from 'jspdf';

// Images
import CertBg from '../../Images/certificate-background.png';
import LogoOlibX3 from '../../Images/logo-olib-white-x3.png';
import CertBadge from '../../Images/cert-badge-x3.png';
import SparkIcon from '../../Images/spark-certification.png';
import DownloadIcon from '../../Images/download-icon.png';
import HcasdLogo from '../../Images/hcas-logo.png';

// Styles
import './TutorialCertificate.css';
import { toTitleCase } from '../../utils/toTitleCase';

/**
 * Load an image from a URL and return a base64 dataURL
 */
const loadImageDataUrl = (url) => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.naturalWidth;
			canvas.height = img.naturalHeight;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0);
			resolve(canvas.toDataURL('image/png'));
		};
		img.onerror = reject;
		img.src = url;
	});
};

/** Utility to format date as “Month Day, Year” */
const formatLongDate = (input) => {
	const d = input instanceof Date ? input : new Date(input);
	return d.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
};

function arrayBufferToBase64(buffer) {
	let binary = '';
	const bytes = new Uint8Array(buffer);
	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}

async function embedFont(doc, nameOnDisk, postScriptName, style = 'normal') {
	const fontUrl = `${window.location.origin}/fonts/Poppins/${nameOnDisk}`;
	const res = await fetch(fontUrl);
	if (!res.ok) throw new Error(`Failed to load ${fontUrl}`);
	const buffer = await res.arrayBuffer();
	const base64 = arrayBufferToBase64(buffer);
	doc.addFileToVFS(nameOnDisk, base64);
	doc.addFont(nameOnDisk, postScriptName, style);
}

/**
 * Generate & download the certificate PDF.
 * @param {string} name  — Recipient’s name
 * @param {Date|string} date  — Certificate date
 */
export async function generateCertificatePDF(
	name,
	date = new Date(),
	institutionCode
) {
	// Preload all images as dataURLs
	const [bgDataUrl, logoDataUrl, badgeIMG, SparkICON, hcasLogo] =
		await Promise.all([
			loadImageDataUrl(CertBg),
			loadImageDataUrl(LogoOlibX3),
			loadImageDataUrl(CertBadge),
			loadImageDataUrl(SparkIcon),
			loadImageDataUrl(HcasdLogo),
		]);

	// 1. Create document
	const doc = new jsPDF({
		orientation: 'landscape',
		unit: 'px',
		format: 'letter',
	});
	const W = doc.internal.pageSize.getWidth();
	const H = doc.internal.pageSize.getHeight();

	//Fonts
	await embedFont(doc, 'Poppins-Bold.ttf', 'Poppins', 'bold');
	await embedFont(doc, 'Poppins-Medium.ttf', 'Poppins', 'medium');
	await embedFont(doc, 'Poppins-Regular.ttf', 'Poppins', 'normal');
	await embedFont(doc, 'Poppins-Bold.ttf', 'Poppins', 'light');

	// 2. Draw background
	doc.addImage(bgDataUrl, 'PNG', 0, 0, W, H);

	// 3. Badge (top-right)
	doc.addImage(badgeIMG, 'PNG', 400, 60, 78, 117);

	if (institutionCode === 'HCAS') {
		doc.addImage(hcasLogo, 'PNG', 125, 30, 117, 35);
	}

	// 4. Logo (top-left)
	const logoW = 86,
		logoH = 36;
	doc.addImage(logoDataUrl, 'PNG', 30, 30, logoW, logoH);

	// Spark Icon (Bottom center)
	doc.addImage(SparkICON, 'PNG', 282, 390, 15, 15);

	// 5. Headings & text
	doc
		.setFont('Poppins', 'bold')
		.setTextColor('#ffffff')
		.setFontSize(40)
		.text('Certificate', 197, 148, { align: 'right' })
		.setFontSize(14)
		.text('AQUINAS ONLINE LIBRARY', 170, 170, { align: 'right' })
		.setFontSize(14)
		.setFont('Poppins', 'normal')
		.text('CERTIFIES THAT', 110, 183, { align: 'right' })
		.setFontSize(32)
		.setFont('Poppins', 'bold')
		.text(toTitleCase(name), 32, 240)
		.setFontSize(14)
		.setFont('Poppins', 'bold')
		.text('Online Orientation Certification', 203, 280, { align: 'right' });

	// 6. Body paragraph (justified)
	const body =
		'This is to certify that the student has successfully completed the ' +
		'Aquinas Library Online Orientation and has demonstrated proficiency ' +
		'in accessing and utilizing the library’s resources. This training is ' +
		'designed to equip students with the necessary skills to effectively ' +
		'search for academic information, fostering the development of ' +
		'essential information literacy competencies that contribute to ' +
		'academic performance and overall success in university life.';

	doc
		.setFontSize(10)
		.setLineHeightFactor(1.5)
		.setTextColor('#ffffff')
		.setFont('Poppins', 'normal');
	const margin = 40,
		textW = W - margin * 2;
	doc
		.text(body, 34, 300, { maxWidth: textW, align: 'justify' })
		.setFont('Poppins', 'light');

	// 7. Date bottom-right
	doc
		.setFontSize(10)
		.setFont('Poppins', 'bold')
		.text(formatLongDate(new Date()), 34, 350, { align: 'left' });

	// Certificado Would....
	doc
		.setFontSize(10)
		.setFont('Poppins', 'bold')
		.text('Certificate would be issued by', 290, 420, { align: 'center' });
	// Aquinas...
	doc
		.setFontSize(10)
		.setFont('Poppins', 'normal')
		.text('Aquinas Online Library Team', 290, 432, { align: 'center' });
	// 8. Save
	doc.save(`${name.replace(/\s+/g, '_')}_Certificate.pdf`);
}

export default function TutorialCertificate({ name, date, institutionCode }) {
	return (
		<>
			<button
				onClick={async () => {
					try {
						await generateCertificatePDF(name, date, institutionCode);
					} catch (err) {
						console.error('PDF generation failed:', err);
					}
				}}
				className='btn-download-certificate'
			>
				Download Certificate
				<span>
					<img
						className='download-icon-certificate'
						src={DownloadIcon}
						alt='Download Icon'
					/>
				</span>
			</button>
		</>
	);
}
